//#region IMPORTS
import {
  InstoreOrder,
  InstoreOrderLineTableRow,
} from "../../models/instoreOrder";
import {
  instoreOrderLinesSelectors,
  upsertInstoreOrderLineRowLocal,
  removeInstoreOrderLineRowLocal,
  removeManyInstoreOrderLineRowLocal,
  upsertManyInstoreOrderLineRowLocal,
} from "../../features/instoreOrders/instoreOrdersSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { CREATE_ORDERLINE_ROW_MODAL } from "../../models/_consts";
import { Fragment, useEffect, useMemo, useState } from "react";
import { InstoreTemplate } from "../../models/instoreTemplate";
import { InstoreTheme } from "../../models/instoreTheme";
import { StoreFormat } from "../../models/storeFormat";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { toast } from "react-toastify";
import InstoreOrderDetailTable from "./InstoreOrderDetailTable";
import InstoreOrderLineForm from "./InstoreOrderLineForm";
import InstoreOrderForm from "./InstoreOrderForm";
import useAdminManager from "../../hooks/useAdminManager";
import MainContainer from "../_layout/MainContainer";
import GoBackButton from "../genericButtons/goBackButton/GoBackButton";
import useHQmanager from "../../hooks/useHQmanager";
import PageHeader from "../genericPageHeader/PageHeader";
import MainModal from "../_layout/MainModal";
import InstoreOrderSummary from "./InstoreOrderSummary";

let tempLocalId = -1;
const getTempId = () => tempLocalId--;
//#endregion

//#region INTERFACE
interface Props {
  instoreOrder?: InstoreOrder;
  readOnly?: boolean;
  create?: boolean;
}
//#endregion

export default function InstoreOrderDetailContainer({
  instoreOrder,
  readOnly = false,
  create = false,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { loading: loadingTemplates } = useAppSelector(
    ({ instoreTemplates }) => instoreTemplates
  );

  const { loading } = useAppSelector(({ instoreOrders }) => instoreOrders);
  const { user } = useAppSelector(({ account }) => account);

  const instoreOrderLines = useAppSelector(
    instoreOrderLinesSelectors.selectAll
  );

  // Roles
  const isAdmin = useAdminManager(user);
  const hqTeam = useHQmanager(user);
  //#endregion

  // Local state
  const [storeFormatToUse, setStoreFormatToUse] = useState<StoreFormat>();
  const [templateToUse, setTemplateToUse] = useState<InstoreTemplate>();
  //#endregion

  useEffect(() => {
    if (instoreOrder) {
      setStoreFormatToUse({
        id: instoreOrder.store_format_id,
        name: instoreOrder.store_format,
        customer_id: 11,
        customer_name: "Rituals",
      });
    }
  }, [instoreOrder]);

  //#region SET TABLE DATA
  const tableData = useMemo(() => {
    if (!instoreOrderLines || !storeFormatToUse) return [];

    let parentIndex = 10000;

    // Group by theme_name
    const themeGroups = instoreOrderLines.reduce<
      Record<string, typeof instoreOrderLines>
    >((acc, item) => {
      const key = item.theme_name ?? `theme_${item.id}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

    const rows: InstoreOrderLineTableRow[] = [];

    for (const themeItems of Object.values(themeGroups)) {
      if (!themeItems.length) continue;

      const themeInfo = themeItems[0];

      // Map all items under this theme as children
      const children: InstoreOrderLineTableRow[] = themeItems.map(
        (item, index) => ({
          id: String(item.id),
          index,
          theme_name: item.theme_name,
          theme_id: item.theme_id,
          article_name: item.article_name,
          store_format_name: item.store_format_name,
          store_format_id: item.store_format_id,
          amount: item.amount,
          parent: false,
        })
      );

      // Parent row
      rows.push({
        id: `${themeInfo.theme_id}`,
        index: parentIndex,
        theme_name: themeInfo.theme_name,
        theme_id: themeInfo.theme_id,
        article_name: themeInfo.theme_name,
        store_format_name: storeFormatToUse.name,
        store_format_id: storeFormatToUse.id,
        amount: 0,
        parent: true,
        children,
      });

      parentIndex += 10000;
    }

    return rows;
  }, [instoreOrderLines, storeFormatToUse]);
  //#endregion

  //#region ADD THEME ROW
  const addThemeRowModalClickHandler = () => {
    dispatch(openModal(CREATE_ORDERLINE_ROW_MODAL));
  };

  const addThemeRowHandler = async (theme: InstoreTheme) => {
    const newParentIndex =
      tableData.length > 0
        ? Math.max(...tableData.map((r) => r.index ?? 0)) + 10000
        : 10000;

    // Persist to Redux store only
    if (storeFormatToUse && templateToUse) {
      // Create a new local backend-like row for the instore order lines array
      const newLocalRow = {
        id: `${theme.id}`,
        template_id: templateToUse.id,
        theme_name: theme.name,
        theme_id: theme.id,
        store_format_name: storeFormatToUse.name,
        store_format_id: storeFormatToUse.id,
        article_name: "enter name",
        amount: 0,
        index: newParentIndex + 1,
      };

      dispatch(upsertInstoreOrderLineRowLocal(newLocalRow));
    }

    // Close modal
    dispatch(openModal(false));
  };
  //#endregion

  //#region ADD ARTICLE ROW
  const addArticleRowHandler = (themeId: number) => {
    // Compute new child index using current localTableData
    const parentRow = tableData.find((r) => r.theme_id === themeId && r.parent);
    const maxChildIndex = parentRow
      ? parentRow.children && parentRow.children.length > 0
        ? Math.max(...parentRow.children.map((c) => c.index ?? 0))
        : (parentRow.index ?? 0) + 1
      : Date.now();

    // Persist the new child to the Redux instoreTemplate.template_rows (local only)
    if (storeFormatToUse && templateToUse) {
      const newLocalRow = {
        id: getTempId(),
        template_id: templateToUse.id,
        theme_name: parentRow?.theme_name ?? "",
        theme_id: themeId,
        store_format_name: storeFormatToUse.name,
        store_format_id: storeFormatToUse.id,
        article_name: "enter a name",
        amount: 0,
        index: maxChildIndex + 1,
      };

      dispatch(upsertInstoreOrderLineRowLocal(newLocalRow));
    }
  };
  //#endregion

  //#region UPDATE CHANGES TO TEMPLATE ROWS
  const saveLocalStateChangesHandler = (
    columnId: string,
    value: any,
    row: InstoreOrderLineTableRow
  ) => {
    if (!instoreOrderLines || !templateToUse) return;
    let rowsToModify = [...instoreOrderLines];

    if (columnId === "amount") {
      const rowToModify = rowsToModify.find((rtm) => String(rtm.id) === row.id);
      if (rowToModify) {
        if (Number(value) === 0) {
          dispatch(removeInstoreOrderLineRowLocal(rowToModify.id));
        } else {
          dispatch(
            upsertInstoreOrderLineRowLocal({
              ...rowToModify,
              amount: Number(value),
            })
          );
        }
      } else {
        if (Number(value) !== 0) {
          dispatch(
            upsertInstoreOrderLineRowLocal({
              id: getTempId(),
              template_id: templateToUse.id,
              theme_id: row.theme_id,
              theme_name: row.theme_name,
              article_name: row.article_name ?? "",
              store_format_id: row.store_format_id,
              store_format_name: row.store_format_name,
              amount: Number(value),
              index: 9999,
              parent: false,
            })
          );
        }
      }

      return;
    } else if (columnId === "article_name") {
      if (!value) return;

      // Update all rows with the same theme_id and article_name
      const affectedRows = rowsToModify
        .filter(
          (rowToModify) =>
            rowToModify.theme_id === row.theme_id &&
            rowToModify.article_name === row.article_name
        )
        .map((r) => ({ ...r, article_name: value }));

      dispatch(upsertManyInstoreOrderLineRowLocal(affectedRows));
      return;
    } else {
      // Nothing found
      toast.warning("No updatable fields have been found");
      return;
    }
  };
  const removeItemsLocalStateChangesHandler = (
    row: InstoreOrderLineTableRow
  ) => {
    // Collect IDs from the row and its children
    function collectIds(node: InstoreOrderLineTableRow): string[] {
      const ids: string[] = [node.id]; // include current node
      node.children?.forEach((child) => {
        ids.push(...collectIds(child));
      });
      return ids;
    }

    const idsToRemove = collectIds(row);

    if (idsToRemove.length > 0) {
      dispatch(removeManyInstoreOrderLineRowLocal(idsToRemove));
    } else {
      toast.warning("No items found to delete");
    }
  };

  //#endregion

  //#region CLEANUP UI
  const cancelCRUDTemplate = () => {
    dispatch(openModal(false));
  };
  //#endregion

  return (
    <Fragment>
      {/* MODALS */}
      <MainModal
        type={CREATE_ORDERLINE_ROW_MODAL}
        closeHandler={cancelCRUDTemplate}
      >
        <InstoreOrderLineForm
          addThemeRowHandler={addThemeRowHandler}
          usedThemes={Array.from(
            new Set(
              (instoreOrderLines ?? [])
                .map((r: any) => (r.theme ?? "").toLowerCase())
                .filter(Boolean)
            )
          )}
          cancel={cancelCRUDTemplate}
        />
      </MainModal>

      {/* BODY */}
      <MainContainer>
        <PageHeader
          disabled={loadingTemplates || loading || readOnly}
          isAdmin={isAdmin}
          hqTeam={hqTeam}
          title={
            create
              ? "Create new order"
              : instoreOrder && !readOnly
              ? `Edit ${instoreOrder.order_code}`
              : instoreOrder && readOnly
              ? `${instoreOrder.order_code}`
              : "Order detail"
          }
        >
          <GoBackButton />
        </PageHeader>

        {readOnly && instoreOrder ? (
          <InstoreOrderSummary
            instoreOrder={instoreOrder}
            orderLineItemsList={
              storeFormatToUse && tableData && tableData.length > 0 ? (
                <InstoreOrderDetailTable
                  saveLocalStateChanges={(
                    columnId: string,
                    value: any,
                    row: InstoreOrderLineTableRow
                  ) => saveLocalStateChangesHandler(columnId, value, row)}
                  addParentRowHandler={addThemeRowModalClickHandler}
                  addChildRowHandler={addArticleRowHandler}
                  deleteRowHandler={(row: InstoreOrderLineTableRow) =>
                    removeItemsLocalStateChangesHandler(row)
                  }
                  storeFormatToUse={storeFormatToUse}
                  readOnly
                  data={tableData}
                />
              ) : null
            }
          />
        ) : (
          <InstoreOrderForm
            instoreOrderLinesFromTemplate={instoreOrderLines}
            setInstoreTemplateHandler={(template: InstoreTemplate) =>
              setTemplateToUse(template)
            }
            setStoreFormatHandler={(format: StoreFormat) =>
              setStoreFormatToUse(format)
            }
            templateToUse={templateToUse}
            instoreOrder={instoreOrder}
            disabled={loadingTemplates || loading}
            orderLineItemsList={
              storeFormatToUse && tableData && tableData.length > 0 ? (
                <InstoreOrderDetailTable
                  saveLocalStateChanges={(
                    columnId: string,
                    value: any,
                    row: InstoreOrderLineTableRow
                  ) => saveLocalStateChangesHandler(columnId, value, row)}
                  addParentRowHandler={addThemeRowModalClickHandler}
                  addChildRowHandler={addArticleRowHandler}
                  deleteRowHandler={(row: InstoreOrderLineTableRow) =>
                    removeItemsLocalStateChangesHandler(row)
                  }
                  storeFormatToUse={storeFormatToUse}
                  disabled={loadingTemplates || loading}
                  data={tableData}
                />
              ) : null
            }
          />
        )}
      </MainContainer>
    </Fragment>
  );
}
